export type RequestCategory = "REZEPT" | "WUNDE" | "VITAL" | "MED" | "NOTFALL" | "SONSTIGES" | "LINKING";
export type RequestPriority = "ROUTINE" | "HEUTE" | "SOFORT";
export type RequestStatus = "NEU" | "GESEHEN" | "IN_BEARBEITUNG" | "ERLEDIGT" | "RUECKFRAGE";
export type RequestDirection = "CARE_TO_PRACTICE" | "PRACTICE_TO_CARE";
export type CarePracticeRequestCommand =
	| "mark_seen"
	| "start_processing"
	| "request_clarification"
	| "resolve"
	| "comment"
	| "reply";

export interface RezeptPayload {
	medName: string;
	dose?: string;
	packSize?: string;
	reason?: string;
	pzn?: string;
}

export interface WundePayload {
	location: string;
	size?: string;
	notes?: string;
	photoUrls?: string[];
}

export interface VitalPayload {
	bp?: string;
	pulse?: number;
	temp?: number;
	spo2?: number;
	weight?: number;
	notes?: string;
	measuredAt?: string;
}

export interface MedPayload {
	changeRequest: string;
	reason?: string;
	notes?: string;
}

export interface NotfallPayload {
	situation: string;
	measuresTaken?: string;
	callbackNumber?: string;
}

export interface SonstigesPayload {
	notes: string;
}

export const PRACTICE_LINKING_REQUEST_TYPE = "patient_linking_request" as const;
export const DEFAULT_LINKING_REQUEST_CATEGORIES = [
	"medications",
	"allergies",
	"diagnoses",
	"medical_history",
] as const;

export const LINKING_REQUEST_CATEGORY_COLOR = {
	text: "text-brand-700 dark:text-brand-300",
	bg: "bg-brand-100 dark:bg-brand-900/30",
	dot: "bg-brand-500",
} as const;

export const LINKING_REQUEST_CATEGORY_TITLES: Record<string, string> = {
	medications: "Medikationsplan",
	allergies: "Allergien",
	diagnoses: "Diagnosen",
	vital_signs: "Vitalwerte",
	medical_history: "Vorerkrankungen",
	doctor_letters: "Arztbriefe",
	lab_values: "Laborwerte",
};

export interface PracticeLinkingRequestPayload {
	requestType: typeof PRACTICE_LINKING_REQUEST_TYPE;
	requestedCategories: string[];
	patientName: string;
	patientDateOfBirth?: string | null;
	targetFacilityName?: string | null;
	notes?: string;
}

// 2026-06-06 Med-Review I2 — Outbound „Medikationsbezogene Rückfrage" (Med-on-resident-list).
// Strangler-Fig: category bleibt 'SONSTIGES' im DB-CHECK, Discriminator lebt im payload-JSONB als
// `request_subtype='med_review_request'`. NICHT category='MED' (das ist ein Änderungswunsch/changeRequest
// → falsche Semantik). Payload trägt NUR Provenienz (medication_id) + Faktum (neutral_note + medication_name)
// — KEINE Dosis/Diagnose/Therapie/reason (MDR-Linie). Praxis-Web rendert via isMedReviewPayload statt „Sonstiges".
export const MED_REVIEW_REQUEST_TYPE = "med_review_request" as const;

export interface MedReviewRequestPayload {
	request_subtype: typeof MED_REVIEW_REQUEST_TYPE;
	source: "voice_workflow" | string;
	flow_state: "pending_practice_review" | string;
	voice_workflow_id?: string | null;
	resident_id: string;
	practice_id: string;
	medication_id: string;
	medication_name: string;
	neutral_note: string;
	// 2026-06-06 Phase M (Codex R2-Lock): aktionierbarer Arzt-Text (faktische Beobachtung + Prüfbitte, MDR-safe
	// gefiltert). Optional — Alt-Rows haben nur neutral_note. Render: review_request_note ?? neutral_note.
	review_request_note?: string | null;
	care_observation_context?: string | null;
	voice_workflow_action_id?: string | null;
}

// Voice Phase 2.6 Step 11b + Phase 2.8 P1 — Cross-Sector Reschedule-Request.
// Discriminator: payload.request_subtype === APPOINTMENT_RESCHEDULE_REQUEST_TYPE.
// Snapshot-Felder (resident_name_snapshot etc.) sind seit Phase 2.8 P1 gesetzt;
// für vor-2.8-Rows können sie NULL sein (Backfill-Migration füllt sie best-effort).
export const APPOINTMENT_RESCHEDULE_REQUEST_TYPE = "appointment_reschedule_request" as const;

export interface AppointmentReschedulePayloadDatetime {
	start_iso: string;
	end_iso: string;
}

export interface AppointmentReschedulePayload {
	request_subtype: typeof APPOINTMENT_RESCHEDULE_REQUEST_TYPE;
	source: "voice_workflow" | string;
	flow_state: "pending_practice_confirm" | string;
	voice_workflow_id?: string | null;
	patient_id?: string | null;
	practice_id?: string | null;
	resident_id?: string | null;
	target_appointment_id?: string | null;
	old_datetime?: AppointmentReschedulePayloadDatetime | null;
	requested_new_datetime?: AppointmentReschedulePayloadDatetime | null;
	requested_date_phrase?: string | null;
	reason?: string | null;
	// Phase 2.8 P1 Snapshot-Felder für Practice-Web-Detail-Rendering.
	// NULL-tolerant: alte Rows ohne Backfill rendern die InfoRow einfach nicht.
	resident_name_snapshot?: string | null;
	resident_room_snapshot?: string | null;
	facility_name_snapshot?: string | null;
	old_appointment_type_snapshot?: string | null;
}

// 2026-05-21 Voice Phase 2.10 — neue Terminanfrage (kein existierender Termin).
// Praxis-Web rendert die Card als „Terminwunsch" via isAppointmentRequestPayload.
// Strangler-Fig: category bleibt 'SONSTIGES' im DB-CHECK, Subtype lebt im Payload.
export const APPOINTMENT_REQUEST_TYPE = "appointment_request" as const;

export interface AppointmentRequestPayload {
	request_subtype: typeof APPOINTMENT_REQUEST_TYPE;
	source: "voice_workflow" | string;
	flow_state: "pending_practice_propose" | string;
	voice_workflow_id?: string | null;
	resident_id: string;
	practice_id: string;
	requested_date_phrase: string;
	requested_iso?: string | null;
	// 2026-05-21 Phase 2.10 — Arztname + Alt-Termin „soweit erkennbar".
	// Heute meist null (Edge-fn-Prompt extrahiert sie noch nicht).
	requested_doctor_name?: string | null;
	original_appointment_phrase?: string | null;
	// 2026-05-22 Phase 2.11 — JSONB-Strangler-Verknüpfung zum erstellten Termin.
	// Gesetzt wenn die Praxis aus dem Terminwunsch einen echten appointment
	// angelegt hat. Keine FK-Spalte (bewusst, kein Migrations-Bedarf).
	appointment_id?: string | null;
	reason?: string | null;
}

export type RequestPayload =
	| RezeptPayload
	| WundePayload
	| VitalPayload
	| MedPayload
	| NotfallPayload
	| SonstigesPayload
	| PracticeLinkingRequestPayload
	| MedReviewRequestPayload
	| AppointmentReschedulePayload
	| AppointmentRequestPayload;

export interface CarePracticeRequest {
	id: string;
	createdAt: string;
	updatedAt: string;
	facilityId: string | null;
	facilityName?: string;
	practiceId: string | null;
	practiceName?: string;
	patientId: string | null;
	patientName?: string;
	residentId: string | null;
	residentName?: string;
	createdBy: string;
	createdByName?: string;
	category: RequestCategory;
	priority: RequestPriority;
	status: RequestStatus;
	direction: RequestDirection;
	subject: string;
	payload: RequestPayload;
	lastComment: string | null;
	lastCommentAt: string | null;
	lastCommentBy: string | null;
	resolvedAt: string | null;
	resolvedBy: string | null;
	resolutionNotes: string | null;
}

export interface RequestTimelineEntry {
	id: string;
	createdAt: string;
	actorName: string;
	command: string;
	fromStatus: RequestStatus | null;
	toStatus: RequestStatus | null;
	comment: string | null;
	resolutionNotes: string | null;
}

export interface CreateRequestInput {
	facilityId?: string;
	practiceId?: string;
	patientId?: string;
	residentId?: string;
	category: RequestCategory;
	priority: RequestPriority;
	subject: string;
	payload: RequestPayload;
}

export interface UpdateRequestInput {
	command: CarePracticeRequestCommand;
	lastComment?: string;
	resolutionNotes?: string;
}

export interface RequestStats {
	neu: number;
	gesehen: number;
	inBearbeitung: number;
	rueckfrage: number;
	total: number;
}

export interface CarePracticeRequestPresentation {
	isLinkingRequest: boolean;
	linkingPayload: PracticeLinkingRequestPayload | null;
	categoryLabel: string;
	categoryColor: { text: string; bg: string; dot: string };
	headline: string;
	requestedCategoryTitles: string[];
	searchText: string;
}

export const CATEGORY_LABELS: Record<RequestCategory, string> = {
	REZEPT: "Rezept",
	WUNDE: "Wundversorgung",
	VITAL: "Vitalwerte",
	MED: "Medikationsänderung",
	NOTFALL: "Notfall",
	SONSTIGES: "Sonstiges",
	LINKING: "Verknüpfung",
};

export const CATEGORY_COLORS: Record<RequestCategory, { text: string; bg: string; dot: string }> = {
	REZEPT: { text: "text-blue-700", bg: "bg-blue-100 dark:bg-blue-900/30", dot: "bg-blue-500" },
	WUNDE: { text: "text-orange-700", bg: "bg-orange-100 dark:bg-orange-900/30", dot: "bg-orange-500" },
	VITAL: { text: "text-emerald-700", bg: "bg-emerald-100 dark:bg-emerald-900/30", dot: "bg-emerald-500" },
	MED: { text: "text-purple-700", bg: "bg-purple-100 dark:bg-purple-900/30", dot: "bg-purple-500" },
	NOTFALL: { text: "text-red-700", bg: "bg-red-100 dark:bg-red-900/30", dot: "bg-red-500" },
	SONSTIGES: { text: "text-neutral-600", bg: "bg-neutral-100 dark:bg-neutral-800", dot: "bg-neutral-400" },
	LINKING: { text: "text-indigo-700", bg: "bg-indigo-100 dark:bg-indigo-900/30", dot: "bg-indigo-500" },
};

export const PRIORITY_LABELS: Record<RequestPriority, string> = {
	ROUTINE: "Routine",
	HEUTE: "Heute",
	SOFORT: "Sofort",
};

export const PRIORITY_COLORS: Record<RequestPriority, { text: string; bg: string }> = {
	ROUTINE: { text: "text-neutral-600", bg: "bg-neutral-100 dark:bg-neutral-800" },
	HEUTE: { text: "text-amber-700", bg: "bg-amber-100 dark:bg-amber-900/30" },
	SOFORT: { text: "text-red-700", bg: "bg-red-100 dark:bg-red-900/30" },
};

export const DIRECTION_LABELS: Record<RequestDirection, string> = {
	CARE_TO_PRACTICE: "Pflege → Praxis",
	PRACTICE_TO_CARE: "Praxis → Pflege",
};

export const STATUS_LABELS: Record<RequestStatus, string> = {
	NEU: "Neu",
	GESEHEN: "Gesehen",
	IN_BEARBEITUNG: "In Bearbeitung",
	ERLEDIGT: "Erledigt",
	RUECKFRAGE: "Rückfrage",
};

export const STATUS_COLORS: Record<RequestStatus, { text: string; bg: string; dot: string }> = {
	NEU: { text: "text-blue-700", bg: "bg-blue-100 dark:bg-blue-900/30", dot: "bg-blue-500" },
	GESEHEN: { text: "text-sky-700", bg: "bg-sky-100 dark:bg-sky-900/30", dot: "bg-sky-500" },
	IN_BEARBEITUNG: { text: "text-amber-700", bg: "bg-amber-100 dark:bg-amber-900/30", dot: "bg-amber-500" },
	ERLEDIGT: { text: "text-emerald-700", bg: "bg-emerald-100 dark:bg-emerald-900/30", dot: "bg-emerald-500" },
	RUECKFRAGE: { text: "text-orange-700", bg: "bg-orange-100 dark:bg-orange-900/30", dot: "bg-orange-500" },
};

export const REQUEST_COMMAND_LABELS: Record<CarePracticeRequestCommand, string> = {
	mark_seen: "Als gesehen markieren",
	start_processing: "In Bearbeitung",
	request_clarification: "Rückfrage",
	resolve: "Erledigen",
	comment: "Kommentar senden",
	reply: "Antworten",
};

export const REQUEST_COMMAND_ALLOWED_FROM: Record<CarePracticeRequestCommand, readonly RequestStatus[]> = {
	mark_seen: ["NEU"],
	start_processing: ["NEU", "GESEHEN", "RUECKFRAGE"],
	request_clarification: ["NEU", "GESEHEN", "IN_BEARBEITUNG"],
	resolve: ["NEU", "GESEHEN", "IN_BEARBEITUNG", "RUECKFRAGE"],
	comment: ["NEU", "GESEHEN", "IN_BEARBEITUNG", "RUECKFRAGE"],
	reply: ["RUECKFRAGE", "IN_BEARBEITUNG"],
};

export const REQUEST_COMMAND_NEXT_STATUS: Record<CarePracticeRequestCommand, RequestStatus | null> = {
	mark_seen: "GESEHEN",
	start_processing: "IN_BEARBEITUNG",
	request_clarification: "RUECKFRAGE",
	resolve: "ERLEDIGT",
	comment: null,
	reply: null,
};

export function canExecuteRequestCommand(
	status: RequestStatus,
	command: CarePracticeRequestCommand,
): boolean {
	return REQUEST_COMMAND_ALLOWED_FROM[command].includes(status);
}

export function getAllowedRequestCommands(status: RequestStatus): CarePracticeRequestCommand[] {
	return (Object.keys(REQUEST_COMMAND_ALLOWED_FROM) as CarePracticeRequestCommand[]).filter(
		(command) => canExecuteRequestCommand(status, command),
	);
}

export function getNextStatusForRequestCommand(
	status: RequestStatus,
	command: CarePracticeRequestCommand,
): RequestStatus {
	return REQUEST_COMMAND_NEXT_STATUS[command] ?? status;
}

export function isPracticeLinkingRequestPayload(
	payload: RequestPayload | null | undefined,
): payload is PracticeLinkingRequestPayload {
	if (!payload || typeof payload !== "object") {
		return false;
	}

	return (
		"requestType" in payload
		&& payload.requestType === PRACTICE_LINKING_REQUEST_TYPE
		&& Array.isArray(payload.requestedCategories)
		&& typeof payload.patientName === "string"
	);
}

// 2026-06-06 Med-Review I2 — Type-Guard. Praxis-Inbox-Renderer
// (apps/practice-web/src/routes/care-requests.tsx) labelt die Card als
// „Medikationsbezogene Rückfrage" statt generisch „Sonstiges".
export function isMedReviewPayload(
	payload: RequestPayload | null | undefined,
): payload is MedReviewRequestPayload {
	if (!payload || typeof payload !== "object") {
		return false;
	}

	return (
		"request_subtype" in payload
		&& payload.request_subtype === MED_REVIEW_REQUEST_TYPE
	);
}

// Voice Phase 2.8 P1 — Type-Guard für Reschedule-Anfragen.
// Strict-Discriminator: nur `request_subtype === 'appointment_reschedule_request'`,
// keine Forderung an Snapshot-Felder (NULL-tolerant für vor-2.8-Backfill-Rows).
export function isAppointmentReschedulePayload(
	payload: RequestPayload | null | undefined,
): payload is AppointmentReschedulePayload {
	if (!payload || typeof payload !== "object") {
		return false;
	}

	return (
		"request_subtype" in payload
		&& payload.request_subtype === APPOINTMENT_RESCHEDULE_REQUEST_TYPE
	);
}

// 2026-05-21 Voice Phase 2.10 — Type-Guard für neue Terminanfrage.
// Praxis-Inbox-Renderer (apps/practice-web/src/routes/care-requests.tsx) prüft
// ihn, um die Card als „Terminwunsch" zu rendern statt generisch „Sonstiges".
export function isAppointmentRequestPayload(
	payload: RequestPayload | null | undefined,
): payload is AppointmentRequestPayload {
	if (!payload || typeof payload !== "object") {
		return false;
	}

	return (
		"request_subtype" in payload
		&& payload.request_subtype === APPOINTMENT_REQUEST_TYPE
	);
}

export function getRequestedCategoryTitles(categories: readonly string[]): string[] {
	return categories.map((category) => LINKING_REQUEST_CATEGORY_TITLES[category] ?? category);
}

export function createPracticeLinkingRequestPayload(input: {
	patientName: string;
	patientDateOfBirth?: string | null;
	targetFacilityName?: string | null;
	notes?: string;
	requestedCategories?: readonly string[];
}): PracticeLinkingRequestPayload {
	return {
		requestType: PRACTICE_LINKING_REQUEST_TYPE,
		requestedCategories: [...(input.requestedCategories ?? DEFAULT_LINKING_REQUEST_CATEGORIES)],
		patientName: input.patientName,
		patientDateOfBirth: input.patientDateOfBirth ?? null,
		targetFacilityName: input.targetFacilityName ?? null,
		notes: input.notes,
	};
}

export function getCarePracticeRequestPresentation(
	request: CarePracticeRequest,
): CarePracticeRequestPresentation {
	const linkingPayload = isPracticeLinkingRequestPayload(request.payload) ? request.payload : null;
	// 2026-06-06 Med-Review I2 — Presentation-Override, damit Listen-Cards „Medikationsbezogene
	// Rückfrage zu <Med>" zeigen statt generisch „Sonstiges". Nur Med-Name (kein Dosis/Therapie).
	const medReviewPayload = isMedReviewPayload(request.payload) ? request.payload : null;
	const requestedCategoryTitles = linkingPayload
		? getRequestedCategoryTitles(linkingPayload.requestedCategories)
		: [];
	const headline = linkingPayload
		? `Verknüpfung für ${linkingPayload.patientName}`
		: medReviewPayload
			? `Medikationsbezogene Rückfrage zu ${medReviewPayload.medication_name}`
			: request.subject;
	const categoryLabel = linkingPayload
		? CATEGORY_LABELS.LINKING
		: medReviewPayload
			? "Medikationsbezogene Rückfrage"
			: CATEGORY_LABELS[request.category];
	const searchText = [
		headline,
		request.subject,
		request.residentName,
		request.practiceName,
		request.facilityName,
		categoryLabel,
		linkingPayload?.patientName,
		linkingPayload?.targetFacilityName,
		medReviewPayload?.medication_name,
		...requestedCategoryTitles,
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();

	return {
		isLinkingRequest: Boolean(linkingPayload),
		linkingPayload,
		categoryLabel,
		categoryColor: linkingPayload ? LINKING_REQUEST_CATEGORY_COLOR : CATEGORY_COLORS[request.category],
		headline,
		requestedCategoryTitles,
		searchText,
	};
}
